import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import {
  ContestantItemContainer,
  CountText,
  SearchWrapper,
  SearchInput,
  AddButtonText,
  ModalContent,
  ContestantsList,
  HeaderContainer,
  TypeList,
  TypeListItem,
  TypeListItemText,
  TypeItemIndicator,
  TabsContainer,
  ContestantItem,
  ContestantItemText,
} from './AddContestants.styles';
import CustomModal from '../../custom-inputs/CustomModal';
import NewGameState from '../../_types/NewGameState';
import Contestant from '../../../_types/Contestant';
import Scope from '../../_types/Scope';
import { GamifyIcon } from '../../../../../components/GamifyIcon';
import { useTeamsQuery } from '../../../../scheduling/SchedulingScreen/api/useTeamsQuery';
import { useUsersQuery } from '../../../../scheduling/SchedulingScreen/api/useUsersQuery';
import {
  Box,
  Avatar,
  CircularProgress,
  Checkbox,
  Theme as MuiTheme,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import styled from '@emotion/styled';
import Fuse from 'fuse.js';
import { CustomInputLabel } from '../../GameFormComponents';
import { LOG } from '../../../../../common/Utilities';
import ConfirmationModal from '../../../../users/ConfirmationModal';

// Styled components for the new UI design with dark mode support
const ContestantsPanel = styled.div<{ theme?: MuiTheme; isDark?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 0;
  margin-top: 4px;
  background-color: ${({ theme, isDark }) =>
    theme?.custom?.background1 || (isDark ? '#121212' : 'white')};
  padding: 12px;
  border-radius: 8px;
  border: 1px solid
    ${({ theme, isDark }) =>
      theme?.custom?.stroke || (isDark ? '#404040' : '#e0e0e0')};
  transition: border-color 0.2s ease, background-color 0.2s ease;
`;

const ContestantPill = styled.div<{
  backgroundColor?: string;
  theme?: MuiTheme;
  isDark?: boolean;
}>`
  display: flex;
  align-items: center;
  background-color: ${({ backgroundColor, theme, isDark }) => {
    if (backgroundColor === '#FF8A65') {
      return theme?.custom?.primary || '#FF6A00';
    }
    return (
      backgroundColor ||
      theme?.custom?.background2 ||
      (isDark ? '#1E1E1E' : '#F1F3F7')
    );
  }};
  border-radius: 8px;
  padding: 6px 16px;
  gap: 6px;
  border: 1px solid
    ${({ theme, isDark }) =>
      theme?.custom?.stroke || (isDark ? '#404040' : '#f1f3f7')};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${({ isDark }) =>
      isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'};
  }
`;

const ContestantId = styled.div<{ theme?: MuiTheme; isDark?: boolean }>`
  font-size: 13px;
  color: ${({ theme, isDark }) =>
    theme?.custom?.text || (isDark ? '#FFFFFF' : '#333')};
  font-family: Manrope;
  font-weight: 500;
`;

const CloseButton = styled.div<{ theme?: MuiTheme; isDark?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ theme, isDark }) =>
    theme?.custom?.textLight || (isDark ? '#B0B0B0' : '#666')};
  background-color: ${({ theme, isDark }) =>
    theme?.custom?.background3 || (isDark ? '#2D2D2D' : '#e0e0e0')};
  border-radius: 50%;
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme?.custom?.primary || '#ff6a00'};
    background-color: ${({ theme, isDark }) =>
      theme?.custom?.background2 || (isDark ? '#404040' : '#eeeeee')};
    transform: scale(1.1);
  }
`;

const AddButton = styled.div<{ theme?: MuiTheme; isDark?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme?.custom?.textCTA || 'white'};
  font-size: 13px;
  font-weight: 500;
  gap: 8px;
  font-family: Manrope;
`;

// Extend the Contestant type to make avatar optional
interface ContestantWithOptionalAvatar extends Omit<Contestant, 'avatar'> {
  avatar?: string;
}

const ViewContainer = styled.div<{ theme?: MuiTheme; isDark?: boolean }>`
  display: flex;
  flex-direction: column;
  background: ${({ theme, isDark }) =>
    theme?.custom?.background1 || (isDark ? '#121212' : 'white')};
  width: 100%;
  transition: background-color 0.2s ease;
`;

const DrawerContent = styled.div<{ theme?: MuiTheme; isDark?: boolean }>`
  flex: 1;
  padding: 24px;
  background: ${({ theme, isDark }) =>
    theme?.custom?.background1 ||
    (isDark ? '#121212' : theme?.palette?.background?.paper || 'white')};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  transition: background-color 0.2s ease;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme, isDark }) =>
      theme?.custom?.background3 || (isDark ? '#2D2D2D' : '#f8f9fa')};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme, isDark }) =>
      theme?.custom?.textLight || (isDark ? '#555555' : '#dee2e6')};
    border-radius: 4px;

    &:hover {
      background: ${({ theme, isDark }) =>
        theme?.custom?.text || (isDark ? '#666666' : '#adb5bd')};
    }
  }
`;

const BracketDrawerHeader = styled.div<{ theme?: MuiTheme; isDark?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid
    ${({ theme, isDark }) =>
      theme?.custom?.stroke || (isDark ? '#404040' : '#f0f0f0')};
`;

const DrawerTitle = styled.h2<{ theme?: MuiTheme; isDark?: boolean }>`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  font-family: Space Grotesk;
  color: ${({ theme, isDark }) =>
    theme?.custom?.text ||
    (isDark ? '#FFFFFF' : theme?.palette?.text?.primary || '#333')};
`;

const CloseIconButton = styled.div<{ theme?: MuiTheme; isDark?: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: background-color 0.2s ease;
  color: ${({ theme, isDark }) =>
    theme?.custom?.text || (isDark ? '#FFFFFF' : '#333')};

  &:hover {
    background-color: ${({ theme, isDark }) =>
      theme?.custom?.background3 ||
      (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)')};
  }
`;

interface Props {
  newGameState: NewGameState;
  onChangeNewGameState: (newGameState: NewGameState) => void;
  initialOpen?: boolean;
  contestants?: Contestant[];
  onContestantSelect?: (contestant: Contestant | undefined) => void;
  isBracketMode?: boolean;
}

const AddContestantsView: React.FC<Props> = ({
  newGameState,
  onChangeNewGameState,
  initialOpen = false,
  contestants = [],
  onContestantSelect,
  isBracketMode = false,
}) => {
  const [openModal, setOpenModal] = useState(initialOpen);
  const [initialSelectedContestants, setInitialSelectedContestants] = useState<
    ContestantWithOptionalAvatar[]
  >([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [displayedItems, setDisplayedItems] = useState<
    ContestantWithOptionalAvatar[]
  >([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [dataInitialized, setDataInitialized] = useState(false);

  // Theme hook for dark mode support
  const theme = useTheme();
  const isDark = theme.custom?.isDark || false;

  // Local state for viewing mode (separate from game scope)
  const [viewMode, setViewMode] = useState<'person' | 'circle'>(
    newGameState.scope === 'user'
      ? 'person'
      : (newGameState.scope as 'person' | 'circle')
  );

  // State for type change confirmation modal
  const [showTypeChangeConfirmation, setShowTypeChangeConfirmation] =
    useState(false);
  const [pendingTypeChange, setPendingTypeChange] = useState<string | null>(
    null
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const ITEMS_PER_PAGE = 20;

  const types = [
    { label: 'People', value: 'person' },
    { label: 'Teams', value: 'circle' },
  ];

  const { data: teams = [], isLoading: isTeamsLoading } = useTeamsQuery();
  const { data: employees = [], isLoading: isUsersLoading } = useUsersQuery();

  // Sync the initialSelectedContestants with current assigneeIDs when modal opens
  useEffect(() => {
    if (
      openModal &&
      !isTeamsLoading &&
      !isUsersLoading &&
      newGameState.assigneeIDs.length > 0
    ) {
      LOG.debug('Modal opened, syncing selected contestants from assigneeIDs');

      const selectedContestants = newGameState.assigneeIDs.map((id) => {
        const contestant: ContestantWithOptionalAvatar = {
          value: id,
          label: id,
        };

        // Find contestant data from teams or employees
        if (newGameState.scope === 'circle') {
          const team = teams.find((t: any) => t.id === id);
          if (team) {
            contestant.label = team.name;
          }
        } else {
          const employee = employees.find((e: any) => e.id === id);
          if (employee) {
            contestant.label = employee.name;
            contestant.avatar = employee.avatar;
          }
        }

        return contestant;
      });

      LOG.debug(
        'Setting initial selected contestants in modal:',
        selectedContestants
      );
      setInitialSelectedContestants(selectedContestants);
    }
  }, [
    openModal,
    newGameState.assigneeIDs,
    newGameState.scope,
    teams,
    employees,
    isTeamsLoading,
    isUsersLoading,
  ]);

  // Add effect to monitor initialSelectedContestants changes
  useEffect(() => {
    LOG.debug(
      'initialSelectedContestants updated:',
      initialSelectedContestants
    );
  }, [initialSelectedContestants]);

  // Debug logging
  useEffect(() => {
    if (newGameState.scope === 'circle') {
      LOG.debug('Teams data loaded:', teams.length, 'teams');
      LOG.debug('Current assigneeIDs:', newGameState.assigneeIDs);
    }
  }, [teams, newGameState.assigneeIDs, newGameState.scope]);

  // No need for type selections cache anymore as we don't store selections between tab changes

  // Make sure we correctly handle person vs user scope naming
  useEffect(() => {
    // For backward compatibility if the scope is set to 'user' instead of 'person'
    if (newGameState.scope === 'user') {
      LOG.debug('Converting user scope to person scope for consistency');
      onChangeNewGameState({
        ...newGameState,
        scope: 'person',
      });
    }
  }, [newGameState, onChangeNewGameState]);

  const [loadingContestants, setLoadingContestants] = useState(
    (contestants && contestants.length > 0) || isTeamsLoading || isUsersLoading
  );

  // Initialize selected contestants from the newGameState when data is loaded
  useEffect(() => {
    if (
      isTeamsLoading ||
      isUsersLoading ||
      dataInitialized ||
      newGameState.assigneeIDs.length === 0
    ) {
      return;
    }

    LOG.debug('Initializing selected contestants', {
      scope: newGameState.scope,
      assigneeIDs: newGameState.assigneeIDs,
      teamsLoaded: teams.length,
      employeesLoaded: employees.length,
    });

    // Convert assigneeIDs to the ContestantWithOptionalAvatar format
    const initialContestants = newGameState.assigneeIDs.map((id) => {
      const contestant: ContestantWithOptionalAvatar = {
        value: id,
        label: id,
      };

      // Try to find the contestant in the loaded data
      if (newGameState.scope === 'circle') {
        const team = teams.find((t: any) => t.id === id);
        if (team) {
          contestant.label = team.name;
          LOG.debug(`Found team ${team.name} for ID ${id}`);
        } else {
          LOG.debug(
            `Could not find team for ID ${id} in ${teams.length} teams`
          );
        }
      } else {
        const employee = employees.find((e: any) => e.id === id);
        if (employee) {
          contestant.label = employee.name;
          contestant.avatar = employee.avatar;
        }
      }

      return contestant;
    });

    if (initialContestants.length > 0) {
      LOG.debug('Setting initial selected contestants', initialContestants);
      setInitialSelectedContestants(initialContestants);
      setDataInitialized(true);
    }
  }, [
    newGameState.assigneeIDs,
    newGameState.scope,
    teams,
    employees,
    isTeamsLoading,
    isUsersLoading,
    dataInitialized,
  ]);

  // For bracket mode, ensure team contestants are available for selection
  useEffect(() => {
    if (!isBracketMode || !onContestantSelect || dataInitialized) return;

    // For bracket mode in edit mode, ensure we extract team data
    if (newGameState.id && newGameState.scope === 'circle') {
      LOG.debug('Bracket mode: Processing existing team data');

      const existingCompetitors: any[] = [];

      const gameData = newGameState as any;

      if (gameData.users?.items) {
        const processedCircleIDs = new Set<string>();

        (gameData.users.items || []).forEach((item: any) => {
          if (
            item?.circleID &&
            item?.circle?.name &&
            !processedCircleIDs.has(item.circleID)
          ) {
            processedCircleIDs.add(item.circleID);
            existingCompetitors.push({
              id: item.circleID,
              value: item.circleID,
              name: item.circle.name,
              avatar: '',
            });
            LOG.debug(`Found team ${item.circle.name} for bracket`);
          }
        });
      }

      // Also check circles.items if available
      if (gameData.circles?.items) {
        const processedCircleIDs = new Set<string>();

        (gameData.circles.items || []).forEach((item: any) => {
          if (item?.circle?.id && !processedCircleIDs.has(item.circle.id)) {
            processedCircleIDs.add(item.circle.id);
            existingCompetitors.push({
              id: item.circle.id,
              value: item.circle.id,
              name: item.circle.name,
              avatar: '',
            });
            LOG.debug(`Found circle ${item.circle.name} for bracket`);
          }
        });
      }

      if (existingCompetitors.length > 0) {
        LOG.debug(`Found ${existingCompetitors.length} teams for bracket mode`);

        onChangeNewGameState({
          ...newGameState,
          competitors: [
            ...(newGameState.competitors || []),
            ...existingCompetitors,
          ],
        });
      }

      setDataInitialized(true);
    }
  }, [
    isBracketMode,
    newGameState,
    onChangeNewGameState,
    onContestantSelect,
    dataInitialized,
  ]);

  const filteredContestants = useMemo(() => {
    if (viewMode === 'circle') {
      LOG.debug(
        `Filtering contestants for teams. ${teams.length} teams available.`
      );

      if (teams.length === 0 && !isTeamsLoading) {
        LOG.debug(
          'Teams data is empty but loading is complete. This could indicate a data loading issue.'
        );
      }

      // For teams, make sure we're mapping the data correctly
      return teams.map((team: any) => {
        LOG.debug(`Processing team: ${team.name} (${team.id})`);
        return {
          label: team.name,
          value: team.id,
          type: 'team',
          avatar: '',
        };
      });
    } else {
      // For person scope (formerly 'user')
      return employees.map((employee: any) => ({
        label: employee.name,
        value: employee.id,
        type: 'person',
        avatar: employee.avatar,
      }));
    }
  }, [viewMode, teams, employees, isTeamsLoading]);

  // Get the display data for selected contestants
  const selectedContestantsDisplay = useMemo(() => {
    // Early return if data is still loading to prevent unnecessary calculations
    if (isTeamsLoading || isUsersLoading) {
      return newGameState.assigneeIDs.map((id) => ({
        value: id,
        label: id,
        avatar: '',
      }));
    }

    return newGameState.assigneeIDs.map((id) => {
      let name = id;
      let avatar = '';

      if (newGameState.scope === 'circle') {
        const team = teams.find((t: any) => t.id === id);
        if (team) {
          name = team.name;
        }
      } else {
        const employee = employees.find((e: any) => e.id === id);
        if (employee) {
          name = employee.name;
          avatar = employee.avatar;
        }
      }

      return {
        value: id,
        label: name,
        avatar,
      };
    });
  }, [
    newGameState.assigneeIDs,
    newGameState.scope,
    teams,
    employees,
    isTeamsLoading,
    isUsersLoading,
  ]);

  // Memoize search Fuse instance for performance
  const searchFuse = useMemo(() => {
    // Only create a new Fuse instance if filtered contestants has meaningful data
    if (!filteredContestants || filteredContestants.length === 0) {
      return new Fuse([], { keys: ['label'], threshold: 0.3 });
    }
    return new Fuse(filteredContestants, {
      keys: ['label'],
      threshold: 0.3,
    });
  }, [filteredContestants]);

  // Reset pagination when search query or contestant type changes
  useEffect(() => {
    // Guard against running this effect when data isn't ready
    if (isTeamsLoading || isUsersLoading) return;

    setPage(0);
    setIsLoading(false);

    const initialItems = searchQuery
      ? searchFuse
          .search(searchQuery)
          .slice(0, ITEMS_PER_PAGE)
          .map((result) => result.item)
      : filteredContestants.slice(0, ITEMS_PER_PAGE);

    setDisplayedItems(initialItems);
    setHasMore(
      searchQuery
        ? searchFuse.search(searchQuery).length > ITEMS_PER_PAGE
        : filteredContestants.length > ITEMS_PER_PAGE
    );
  }, [
    searchQuery,
    filteredContestants,
    searchFuse,
    isTeamsLoading,
    isUsersLoading,
  ]);

  const loadMoreItems = useCallback(() => {
    if (isLoading) return;

    setIsLoading(true);

    const nextPage = page + 1;
    const endIndex = (nextPage + 1) * ITEMS_PER_PAGE;

    setTimeout(() => {
      let newItems;
      if (searchQuery) {
        const results = searchFuse
          .search(searchQuery)
          .map((result) => result.item);
        newItems = results.slice(0, endIndex);
        setHasMore(results.length > endIndex);
      } else {
        newItems = filteredContestants.slice(0, endIndex);
        setHasMore(filteredContestants.length > endIndex);
      }

      setDisplayedItems(newItems);
      setPage(nextPage);
      setIsLoading(false);
    }, 300);
  }, [isLoading, page, searchQuery, filteredContestants, searchFuse]);

  // Store the most recent handleScroll function in a ref to avoid recreating it constantly
  const handleScrollRef = useRef<() => void>();
  handleScrollRef.current = () => {
    if (!containerRef.current || isLoading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadMoreItems();
    }
  };

  // The actual scroll handler that uses the ref
  const handleScroll = useCallback(() => {
    handleScrollRef.current?.();
  }, []);

  const checkIsInitialContestantSelected = useCallback(
    (contestant: ContestantWithOptionalAvatar): boolean => {
      // Check if contestant is in initialSelectedContestants
      const isInInitialSelected = initialSelectedContestants.some(
        (initialContestant) => initialContestant.value === contestant.value
      );

      // Also check if contestant is in assigneeIDs (current selected contestants)
      const isInAssigneeIDs = newGameState.assigneeIDs.includes(
        contestant.value
      );

      return isInInitialSelected || isInAssigneeIDs;
    },
    [initialSelectedContestants, newGameState.assigneeIDs]
  );

  const onRemoveContestant = useCallback(
    (contestant: Contestant) => {
      const newAssigneeIds = newGameState.assigneeIDs.filter(
        (id) => id !== contestant.value
      );

      const newCompetitors = newGameState.competitors.filter(
        (comp) =>
          comp.id !== contestant.value && comp.value !== contestant.value
      );

      const newAssigneeNames = newAssigneeIds.map((id) => {
        // Use a direct find instead of depending on a derived state
        if (newGameState.scope === 'circle') {
          const team = teams.find((t: any) => t.id === id);
          return team ? team.name : id;
        } else {
          const employee = employees.find((e: any) => e.id === id);
          return employee ? employee.name : id;
        }
      });

      // Simply update the state without caching
      onChangeNewGameState({
        ...newGameState,
        assigneeIDs: newAssigneeIds,
        competitors: newCompetitors,
        assigneeNames: newAssigneeNames,
      });

      // Also update initialSelectedContestants to stay in sync
      setInitialSelectedContestants((prev) =>
        prev.filter((c) => c.value !== contestant.value)
      );
    },
    [newGameState, onChangeNewGameState, teams, employees]
  );

  const onSelectContestant = useCallback(
    (selectedContestant: ContestantWithOptionalAvatar) => {
      if (isBracketMode && onContestantSelect) {
        // Convert to Contestant type by ensuring avatar is a string
        onContestantSelect({
          ...selectedContestant,
          avatar: selectedContestant.avatar || '',
        });
        return;
      }

      if (checkIsInitialContestantSelected(selectedContestant)) {
        setInitialSelectedContestants((prev) =>
          prev.filter(
            (contestant) => contestant.value !== selectedContestant.value
          )
        );
        return;
      }

      setInitialSelectedContestants((prev) => [...prev, selectedContestant]);
    },
    [checkIsInitialContestantSelected, isBracketMode, onContestantSelect]
  );

  const onAddContestants = useCallback(() => {
    const formattedContestants = initialSelectedContestants.map(
      (contestant) => ({
        id: contestant.value,
        name: contestant.label,
        avatar: contestant.avatar || '',
      })
    );

    const newAssigneeIDs = initialSelectedContestants.map(
      (contestant) => contestant.value
    );

    const newAssigneeNames = initialSelectedContestants.map(
      (contestant) => contestant.label
    );

    // Simply update the game state with the new selections
    onChangeNewGameState({
      ...newGameState,
      competitors: formattedContestants,
      assigneeIDs: newAssigneeIDs,
      assigneeNames: newAssigneeNames,
    });

    setOpenModal(false);
    setSearchQuery('');
    // Don't clear initialSelectedContestants so they remain checked when modal reopens
  }, [initialSelectedContestants, newGameState, onChangeNewGameState]);

  // Function to actually apply the type change after confirmation
  const applyTypeChange = useCallback(
    (newType: string) => {
      // When switching tab types, we should always clear all selections
      // Because teams are only for team games, and people only for person games
      LOG.debug(
        `Changing contestant type to ${newType}, clearing all existing selections`
      );

      onChangeNewGameState({
        ...newGameState,
        scope: newType as Scope,
        isCircleOnly: (newType as Scope) === 'circle',
        // Always clear selections when changing type - no caching between tabs
        assigneeIDs: [],
        competitors: [],
        assigneeNames: [],
        rewardDisposition: 'winner',
      });

      // Reset initialSelectedContestants when changing types
      setInitialSelectedContestants([]);

      // Reset confirmation state
      setShowTypeChangeConfirmation(false);
      setPendingTypeChange(null);
    },
    [newGameState, onChangeNewGameState]
  );

  const onChangeType = useCallback((newType: string) => {
    // Switch view mode without changing the game scope
    setViewMode(newType as 'person' | 'circle');
  }, []);

  // Update loading state when data is available
  useEffect(() => {
    // Only update the loading state if we're not already in a loading state
    // This prevents causing too many re-renders
    if (!isTeamsLoading && !isUsersLoading) {
      setLoadingContestants(false);
    }
  }, [isTeamsLoading, isUsersLoading]);

  // Separate effect for checking if contestants match from API data
  useEffect(() => {
    // Skip this logic during initial loading or when assigneeIDs is empty
    if (
      isTeamsLoading ||
      isUsersLoading ||
      newGameState.assigneeIDs.length === 0
    ) {
      return;
    }

    // We have data and assigneeIDs, check if all IDs can be found
    const allFound = newGameState.assigneeIDs.every((id) => {
      if (newGameState.scope === 'circle') {
        return teams.some((team: any) => team.id === id);
      } else {
        return employees.some((emp: any) => emp.id === id);
      }
    });

    // Only update if necessary to avoid unnecessary re-renders
    if (!allFound) {
      setLoadingContestants(true);
    } else {
      setLoadingContestants(false);
    }
  }, [
    newGameState.assigneeIDs,
    newGameState.scope,
    teams,
    employees,
    isTeamsLoading,
    isUsersLoading,
  ]);

  useEffect(() => {
    if (!newGameState.id || newGameState.scope !== 'circle') {
      return;
    }

    console.log('TEAM DATA DEBUG - Current newGameState:', {
      id: newGameState.id,
      scope: newGameState.scope,
      hasUsers: !!(newGameState as any).users?.items?.length,
      hasCircles: !!(newGameState as any).circles?.items?.length,
      assigneeIDs: newGameState.assigneeIDs,
      competitors: newGameState.competitors,
    });

    const gameData = newGameState as any;
    if (gameData.users?.items) {
      console.log(
        'TEAM DATA DEBUG - users.items sample:',
        gameData.users.items.slice(0, 2)
      );
    }

    if (gameData.users?.items && !dataInitialized) {
      console.log('TEAM DATA DEBUG - Processing team data directly');

      const teamIds: string[] = [];
      const teamData: any[] = [];

      // Process each user item to extract team info
      gameData.users.items.forEach((item: any) => {
        if (item?.circleID && item?.circle?.name) {
          // Check if we've already processed this team
          if (!teamIds.includes(item.circleID)) {
            teamIds.push(item.circleID);
            teamData.push({
              id: item.circleID,
              value: item.circleID,
              name: item.circle.name,
              avatar: '',
            });
            console.log(
              `TEAM DATA DEBUG - Found team: ${item.circle.name} (${item.circleID})`
            );
          }
        }
      });

      // Directly update the state if we found teams
      if (teamIds.length > 0) {
        console.log(
          `TEAM DATA DEBUG - Found ${teamIds.length} teams, updating state`
        );
        onChangeNewGameState({
          ...newGameState,
          assigneeIDs: teamIds,
          competitors: teamData,
          assigneeNames: teamData.map((t) => t.name),
        });
        setDataInitialized(true);
      }
    }
  }, [newGameState, onChangeNewGameState, dataInitialized]);

  const renderContestantItem = useCallback(
    (contestant: ContestantWithOptionalAvatar) => (
      <ContestantItemContainer
        key={contestant.value}
        theme={theme}
        isDark={isDark}
        onClick={() => onSelectContestant(contestant)}
      >
        <ContestantItem
          isSelected={checkIsInitialContestantSelected(contestant)}
          theme={theme}
          isDark={isDark}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar src={contestant.avatar} sx={{ width: 30, height: 30 }}>
              {contestant.label.charAt(0)}
            </Avatar>
            <ContestantItemText theme={theme} isDark={isDark}>
              {contestant.label}
            </ContestantItemText>
          </Box>
          <Checkbox
            checked={checkIsInitialContestantSelected(contestant)}
            sx={{
              padding: 0,
              color: theme?.custom?.stroke || (isDark ? '#555555' : '#D7D9D6'),
              '&.Mui-checked': {
                color: theme?.custom?.primary || '#FF6A00',
              },
              '&:hover': {
                backgroundColor: isDark
                  ? `${theme?.custom?.primary || '#FF6A00'}20`
                  : `${theme?.custom?.primary || '#FF6A00'}10`,
              },
              '& .MuiSvgIcon-root': {
                fontSize: 20,
              },
            }}
          />
        </ContestantItem>
      </ContestantItemContainer>
    ),
    [checkIsInitialContestantSelected, onSelectContestant, theme, isDark]
  );

  const renderTypeSelectionTabs = () => (
    <TabsContainer theme={theme} isDark={isDark}>
      <TypeList>
        {types.map((type) => {
          const isActive = viewMode === type.value;
          return (
            <TypeListItem
              key={type.value}
              onClick={() => onChangeType(type.value)}
            >
              <TypeListItemText
                isActive={isActive}
                theme={theme}
                isDark={isDark}
              >
                {type.label}
              </TypeListItemText>
              <TypeItemIndicator
                isActive={isActive}
                theme={theme}
                isDark={isDark}
              />
            </TypeListItem>
          );
        })}
      </TypeList>
    </TabsContainer>
  );

  const renderContent = () => (
    <>
      {renderTypeSelectionTabs()}
      <HeaderContainer>
        <CountText theme={theme} isDark={isDark}>
          All ({filteredContestants.length})
        </CountText>
        {!isBracketMode && (
          <AddButtonText
            theme={theme}
            isDark={isDark}
            onClick={onAddContestants}
          >
            Add ({initialSelectedContestants.length})
          </AddButtonText>
        )}
      </HeaderContainer>

      <SearchWrapper theme={theme} isDark={isDark}>
        <SearchIcon
          fontSize="small"
          sx={{
            color:
              theme?.custom?.textLight ||
              (isDark ? '#B0B0B0' : 'rgba(0, 0, 0, 0.5)'),
          }}
        />
        <SearchInput
          theme={theme}
          isDark={isDark}
          fullWidth
          variant="standard"
          placeholder="Who would you like to add?"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            disableUnderline: true,
            sx: {
              color: theme?.custom?.text || (isDark ? '#FFFFFF' : '#333'),
              fontFamily: 'Manrope',
              '&::placeholder': {
                color:
                  theme?.custom?.textLight || (isDark ? '#B0B0B0' : '#757575'),
                opacity: 1,
              },
            },
          }}
        />
      </SearchWrapper>

      <ContestantsList
        ref={containerRef}
        onScroll={handleScroll}
        theme={theme}
        isDark={isDark}
      >
        {isTeamsLoading || isUsersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress
              size={20}
              style={{
                marginRight: '8px',
                color: theme?.custom?.primary || '#FF6A00',
              }}
            />
            <span>Loading contestants...</span>
          </Box>
        ) : displayedItems.length > 0 ? (
          displayedItems.map((contestant) => renderContestantItem(contestant))
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <ContestantItemText
              style={{
                color:
                  theme?.custom?.textLight || (isDark ? '#B0B0B0' : '#757575'),
              }}
            >
              No contestants available
            </ContestantItemText>
          </Box>
        )}

        {hasMore && !isLoading && displayedItems.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <ContestantItemText
              style={{
                color:
                  theme?.custom?.textLight || (isDark ? '#B0B0B0' : '#757575'),
              }}
            >
              Scroll for more
            </ContestantItemText>
          </Box>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress
              size={24}
              sx={{
                color: theme?.custom?.primary || '#FF6A00',
              }}
            />
          </Box>
        )}
      </ContestantsList>
    </>
  );

  const renderContestantsList = () => (
    <div style={{ marginBottom: '16px' }}>
      <CustomInputLabel
        style={{
          marginBottom: '4px',
          color: theme?.custom?.text || (isDark ? '#FFFFFF' : '#333'),
        }}
      >
        Contestants
      </CustomInputLabel>
      <ContestantsPanel theme={theme} isDark={isDark}>
        {loadingContestants ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 16px',
              color: theme?.custom?.text || (isDark ? '#FFFFFF' : '#333'),
              fontFamily: 'Manrope',
              fontSize: '14px',
            }}
          >
            <CircularProgress
              size={20}
              style={{
                marginRight: '8px',
                color: theme?.custom?.primary || '#FF6A00',
              }}
            />
            <span>Loading contestants...</span>
          </div>
        ) : (
          <>
            {selectedContestantsDisplay.map((contestant) => (
              <ContestantPill
                key={contestant.value}
                theme={theme}
                isDark={isDark}
              >
                <Avatar
                  src={contestant.avatar}
                  sx={{
                    width: 20,
                    height: 20,
                    bgcolor: contestant.avatar
                      ? undefined
                      : theme?.custom?.background3 ||
                        (isDark ? '#404040' : '#aaaaaa'),
                    fontSize: '12px',
                    color: theme?.custom?.text || (isDark ? '#FFFFFF' : '#333'),
                  }}
                >
                  {contestant.label.charAt(0)}
                </Avatar>
                <ContestantId theme={theme} isDark={isDark}>
                  {contestant.label}
                </ContestantId>
                <CloseButton
                  theme={theme}
                  isDark={isDark}
                  onClick={() =>
                    onRemoveContestant({
                      label: contestant.label,
                      value: contestant.value,
                      avatar: contestant.avatar || '',
                    })
                  }
                >
                  <CloseIcon
                    fontSize="small"
                    style={{
                      fontSize: '12px',
                      color: 'inherit',
                    }}
                  />
                </CloseButton>
              </ContestantPill>
            ))}
          </>
        )}

        <ContestantPill
          style={{ marginLeft: '4px' }}
          backgroundColor="#FF8A65"
          theme={theme}
          isDark={isDark}
          onClick={() => {
            // Ensure the modal opens with the current contestants pre-selected
            if (
              !isTeamsLoading &&
              !isUsersLoading &&
              newGameState.assigneeIDs.length > 0
            ) {
              const currentContestants = newGameState.assigneeIDs.map((id) => {
                const contestant: ContestantWithOptionalAvatar = {
                  value: id,
                  label: id,
                };

                if (newGameState.scope === 'circle') {
                  const team = teams.find((t: any) => t.id === id);
                  if (team) contestant.label = team.name;
                } else {
                  const employee = employees.find((e: any) => e.id === id);
                  if (employee) {
                    contestant.label = employee.name;
                    contestant.avatar = employee.avatar;
                  }
                }

                return contestant;
              });

              setInitialSelectedContestants(currentContestants);
            }
            setOpenModal(true);
          }}
        >
          <AddButton theme={theme} isDark={isDark}>
            <GamifyIcon icon="add" color="white" height={16} width={16} />
            Add
          </AddButton>
        </ContestantPill>
      </ContestantsPanel>
    </div>
  );

  return (
    <ViewContainer theme={theme} isDark={isDark}>
      {isBracketMode ? (
        <DrawerContent theme={theme} isDark={isDark}>
          <BracketDrawerHeader theme={theme} isDark={isDark}>
            <DrawerTitle theme={theme} isDark={isDark}>
              Add people / team to this game
            </DrawerTitle>
            {onContestantSelect && (
              <CloseIconButton
                theme={theme}
                isDark={isDark}
                onClick={() => onContestantSelect(undefined)}
              >
                <CloseIcon />
              </CloseIconButton>
            )}
          </BracketDrawerHeader>
          {renderContent()}
        </DrawerContent>
      ) : (
        <>
          {renderContestantsList()}
          <CustomModal
            width={800}
            isOpen={openModal}
            title="Add Contestants"
            onClose={() => {
              // Just close the modal - selected contestants will be synced when reopened
              setOpenModal(false);
              setSearchQuery('');
            }}
          >
            <ModalContent theme={theme} isDark={isDark}>
              {renderContent()}
            </ModalContent>
          </CustomModal>

          {/* Type Change Confirmation Modal */}
          <ConfirmationModal
            open={showTypeChangeConfirmation}
            onClose={() => {
              setShowTypeChangeConfirmation(false);
              setPendingTypeChange(null);
            }}
            title="Change Game Type"
            message={
              pendingTypeChange === 'person'
                ? 'Switching from Team to Person will change this to a Person game type. Any selected teams will be cleared. Are you sure you want to continue?'
                : 'Switching from Person to Team will change this to a Team game type. Any selected people will be cleared. Are you sure you want to continue?'
            }
            onConfirm={() => {
              if (pendingTypeChange) {
                applyTypeChange(pendingTypeChange);
              }
            }}
            confirmationButtonText="Continue"
            isLoading={false}
          />
        </>
      )}
    </ViewContainer>
  );
};

export default AddContestantsView;
